import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';

const FormAssemblyCallbackPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, session } = useAuth(); // Get session from AuthContext
  const [message, setMessage] = useState('Processing FormAssembly authentication...');
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [hasTriedProcessing, setHasTriedProcessing] = useState(false);
  const [localCode, setLocalCode] = useState<string | null>(null);
  const [localState, setLocalState] = useState<string | null>(null);

  const processCallback = useCallback(async (code: string, stateParam: string | null) => {
    if (!session) {
      setMessage('User session not fully available. Please wait or retry.');
      setError('User session not fully available.');
      setIsProcessing(false);
      toast({
        variant: "destructive",
        title: "Session Error",
        description: "User session details are not fully available. Cannot finalize connection.",
        duration: 7000,
      });
      return;
    }

    setMessage('Finalizing FormAssembly connection...');
    setIsProcessing(true);
    if (!stateParam) {
      const stateError = 'OAuth state parameter missing. Please restart the connection.';
      logger.error('[FA Callback] State parameter missing during processCallback.');
      setMessage(stateError);
      setError(stateError);
      toast({
        variant: "destructive",
        title: "Invalid OAuth State",
        description: stateError,
        duration: 7000,
      });
      setIsProcessing(false);
      sessionStorage.removeItem('fa_oauth_state');
      return;
    }
    try {
      logger.debug('[FA Callback] Invoking formassembly-oauth-callback with code/state:', { code, stateParam });
      const { data, error: invokeError } = await supabase.functions.invoke(
        'formassembly-oauth-callback',
        { body: { code, state: stateParam } }
      );

      if (invokeError) {
        const invokeErrorMessage = `Failed to connect FormAssembly account: ${invokeError.message}`;
        logger.error('[FA Callback] Invoke error:', invokeError);
        setMessage(invokeErrorMessage);
        setError(invokeErrorMessage);
        toast({
          variant: "destructive",
          title: "Connection Finalization Failed",
          description: `Could not finalize FormAssembly connection: ${invokeError.message}`,
          duration: 7000,
        });
      } else {
        logger.debug('[FA Callback] Invoke success:', data);
        setMessage(data.message || 'FormAssembly account connected successfully! Redirecting...');
        toast({
          title: "Connection Successful!",
          description: "Your FormAssembly account has been connected.",
        });
        sessionStorage.removeItem('fa_oauth_state');
        setTimeout(() => navigate('/', { replace: true }), 2000); // Changed redirect to '/'
      }
    } catch (e: unknown) {
      const unexpectedError = `An unexpected error occurred: ${e instanceof Error ? e.message : 'Unknown error'}`;
      logger.error('[FA Callback] Unexpected error in processCallback:', e);
      setMessage(unexpectedError);
      setError(unexpectedError);
      toast({
        variant: "destructive",
        title: "Unexpected Connection Error",
        description: e instanceof Error ? e.message : "An unexpected error occurred during callback processing.",
        duration: 7000,
      });
    } finally {
      setIsProcessing(false);
      sessionStorage.removeItem('fa_oauth_state');
    }
  }, [session, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newCode = params.get('code');
    const newState = params.get('state');
    const storedState = sessionStorage.getItem('fa_oauth_state');
    const authError = params.get('error');
    const errorDescription = params.get('error_description');

    if (newCode && newCode !== localCode) {
      setLocalCode(newCode);
    }

    if (newState && newState !== localState) {
      setLocalState(newState);
    } else if (!newState && storedState && storedState !== localState) {
      setLocalState(storedState);
    }

    if (authError) {
      const fullError = `Error from FormAssembly: ${errorDescription || authError}`;
      logger.error('[FA Callback] Auth error from FormAssembly:', fullError);
      setMessage(fullError);
      setError(fullError);
      setIsProcessing(false);
      toast({
        variant: "destructive",
        title: "FormAssembly Connection Denied",
        description: `Details: ${errorDescription || authError}`,
        duration: 7000,
      });
      return;
    }

    if (!newState && !storedState && newCode) {
      const missingStateError = 'Security check failed: state parameter missing.';
      logger.error('[FA Callback] State parameter missing while code present.');
      setMessage(missingStateError);
      setError(missingStateError);
      setIsProcessing(false);
      toast({
        variant: "destructive",
        title: "FormAssembly Connection Failed",
        description: missingStateError,
        duration: 7000,
      });
      return;
    }

    if (!newCode && !authError) {
      const missingCodeError = 'Authorization code not found in URL. Cannot complete connection.';
      logger.warn('[FA Callback] Authorization code missing and no auth error.');
      if (hasTriedProcessing || (!user || !session)) {
        setMessage(missingCodeError);
        setError(missingCodeError);
        toast({
          variant: "destructive",
          title: "FormAssembly Connection Failed",
          description: "Authorization code is missing from the callback.",
          duration: 7000,
        });
      }
      setIsProcessing(false);
      return;
    }
    
    // Core processing logic
    if (user && session && newCode && (newState || storedState) && !hasTriedProcessing) {
      setHasTriedProcessing(true);
      const effectiveState = newState || storedState;
      logger.debug('[FA Callback] User and session available, proceeding to process callback with code/state:', { newCode, state: effectiveState });
      processCallback(newCode, effectiveState);
    } else if ((!user || !session) && newCode && !hasTriedProcessing) {
      logger.debug('[FA Callback] Waiting for user authentication session to complete...');
      setMessage('Waiting for user authentication session to complete before processing...');
      setIsProcessing(true); 
    } else if (hasTriedProcessing && newCode) {
      logger.debug('[FA Callback] Already tried processing. Current message:', message);
    }

  }, [location, user, session, hasTriedProcessing, processCallback, localCode, localState, message]);

  const retryProcessing = () => {
    if (!localCode || !(localState || sessionStorage.getItem('fa_oauth_state'))) {
      toast({
        variant: "destructive",
        title: "Cannot Retry",
        description: "Authorization details are missing. Please try the connection process from the beginning.",
      });
      return;
    }
    if (!user || !session) {
      toast({
        title: "Session Not Ready",
        description: "User session is not yet available. Please wait a moment and try again.",
      });
      setMessage('Retrying: Waiting for user session...');
      setIsProcessing(true); // Show loader while waiting for session
      return;
    }
    setIsProcessing(true);
    setError(null);
    setMessage('Retrying FormAssembly authentication...');
    const effectiveState = localState || sessionStorage.getItem('fa_oauth_state');
    logger.debug('[FA Callback] Retry button clicked. Processing with code/state:', { localCode, state: effectiveState });
    processCallback(localCode, effectiveState);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <div className="bg-card p-8 rounded-lg shadow-md text-center max-w-md w-full">
        {isProcessing && !error && (
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-6" />
        )}
        <h1 className="text-2xl font-semibold mb-4">
          {isProcessing && !error ? 'Connecting to FormAssembly' : (error ? 'Connection Problem' : 'Connection Status')}
        </h1>
        <p className={`text-muted-foreground mb-6 ${error ? 'text-red-500' : (message.includes('success') ? 'text-green-600' : 'text-muted-foreground')}`}>{message}</p>
        
        {!isProcessing && (
          <div className="space-y-3">
            {error && (
              <Button onClick={retryProcessing} className="w-full mb-2">
                Retry Connection
              </Button>
            )}
            <Button onClick={() => navigate('/', { replace: true })} variant={error ? "outline" : "default"} className="w-full">
              {error ? 'Back to Dashboard' : 'Continue to Dashboard'}
            </Button>
          </div>
        )}
        {isProcessing && error && (
           <div className="mt-4">
             <Button onClick={() => navigate('/', { replace: true })} variant="outline" className="w-full">
                Back to Dashboard
            </Button>
           </div>
        )}
      </div>
    </div>
  );
};

export default FormAssemblyCallbackPage;
